import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toLocalISOString } from 'utils/date';
import {
  getRosterCycleEndResult,
  PlanningCycleRound,
  POSTBody,
  RosterCycleEndResult
} from 'RestAPIs/cost-centers/roster-cycle';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import { Dialog } from 'components/shared';
import { RecurrenceRuleType } from 'components/shared/RecurrenceRule';
import useSaveRosterStateSettings from 'components/roster-portal/plan-units/Settings/hooks/useSaveRosterStateSettings';
import PlanningCycleDialogContent from './PlanningCycleDialogContent';
import dayjs from 'dayjs';
import 'dayjs/locale/en'; // Import English locale

const PlanningCycleDialogSyles = styled.div`
  min-width: 300px;
  min-height: 300px;

  .inline-input {
    width: 50px;
    margin: 0 10px;
    input {
      text-align: center;
    }
  }
  .padding-bottom,
  .recurrence-rule {
    padding-bottom: 20px;
  }
  .settings {
    min-width: 500px;
  }
  .summary {
    min-width: 400px;
  }
`;

type PlanningCycle = RecurrenceRuleType & {
  cycleWindow: number;
  rounds: PlanningCycleRound[];
};

type Props = {
  open: boolean;
  onHidden: (wasSaved: boolean) => void;
  type: 'PUBLISHED' | 'VISIBLE';
  currentPlanningCycle?: PlanningCycle | null;
  costCenterId: number;
};

export default function PlanningCycleDialog({
  open = true,
  onHidden,
  type,
  currentPlanningCycle = null,
  costCenterId
}: Props) {
  const getRecurrenceRule = (
    currentPlanningCycle: PlanningCycle | null
  ): RecurrenceRuleType | null => {
    if (currentPlanningCycle) {
      return {
        start: currentPlanningCycle.start,
        type: currentPlanningCycle.type,
        interval: currentPlanningCycle.interval,
        weekDays: currentPlanningCycle.weekDays
          ? currentPlanningCycle.weekDays
          : [dayjs().locale('en').format('dddd').toUpperCase()],
        dayOfMonth: currentPlanningCycle.dayOfMonth,
        setPosition: currentPlanningCycle.setPosition
      };
    }
    return null;
  };

  const { t } = useTranslation('common');
  const [recurrenceRule, setRecurrenceRule] =
    useState<RecurrenceRuleType | null>(
      getRecurrenceRule(currentPlanningCycle)
    );

  const [cycleWindow, setCycleWindow] = useState(
    currentPlanningCycle?.cycleWindow || 1
  );
  const [rounds, setRounds] = useState(currentPlanningCycle?.rounds || []);
  const [rosterCycles, setRosterCycles] = useState<
    RosterCycleEndResult[] | null
  >(null);
  const { mutate: saveRosterSettings } = useSaveRosterStateSettings();

  const getRosterCycles = useCallback(
    debounce((planningCycle: PlanningCycle) => {
      const body: POSTBody = {
        rosterState: type,
        enabled: true,
        recurrenceRule: {
          start: toLocalISOString(
            dayjs(planningCycle.start).startOf('day').toDate()
          ),
          periodType: planningCycle.type,
          interval: planningCycle.interval,
          dayInWeekSet: planningCycle.weekDays,
          dayOfMonth: planningCycle.dayOfMonth,
          setPosition: planningCycle.setPosition
        },
        cycleWindow: planningCycle.cycleWindow,
        rounds: planningCycle.rounds
      };

      getRosterCycleEndResult(body, costCenterId).then(({ data }) =>
        setRosterCycles(data)
      );
    }, 500),
    [setRosterCycles, costCenterId, type]
  );

  const handleSave = () => {
    if (
      !recurrenceRule ||
      !recurrenceRule.start ||
      !recurrenceRule.type ||
      !recurrenceRule.interval
    )
      return;

    const planningCyclePayload: POSTBody = {
      rosterState: type,
      enabled: true,
      recurrenceRule: {
        start: toLocalISOString(
          dayjs(recurrenceRule.start!).startOf('day').toDate()
        ),
        periodType: recurrenceRule.type!,
        interval: recurrenceRule.interval!,
        dayInWeekSet: recurrenceRule?.weekDays,
        dayOfMonth: recurrenceRule?.dayOfMonth,
        setPosition: recurrenceRule?.setPosition
      },
      cycleWindow: cycleWindow,
      rounds: rounds.map((round) => ({
        startIndex: round.startIndex,
        endIndex: round.endIndex,
        autoConnect: round.autoConnect
      }))
    };

    saveRosterSettings(
      { costCenterId, planningCyclePayload },
      {
        onSuccess: () => {
          onHidden(true);
        },
        onError: (error) => {
          console.error('Failed to save planning cycle:', error);
        }
      }
    );
  };

  const addRound = () => {
    const lastRound = rounds.length > 0 ? rounds[rounds.length - 1] : undefined;
    const start = (lastRound?.endIndex ?? 0) + 1;
    const newRound = {
      startIndex: start,
      endIndex: start + 6,
      autoConnect: false
    };
    setRounds([...rounds, newRound]);
  };

  const updateRound = (index: number, round: PlanningCycleRound) => {
    setRounds([...rounds.slice(0, index), round, ...rounds.slice(index + 1)]);
  };

  const deleteRound = (index) => {
    setRounds(rounds.filter((item, idx) => idx !== index));
  };

  useEffect(() => {
    if (currentPlanningCycle) {
      setCycleWindow(currentPlanningCycle.cycleWindow || 1);
    }
  }, [currentPlanningCycle]);

  useEffect(() => {
    if (recurrenceRule) {
      const newPlanningCycle = { ...recurrenceRule, cycleWindow, rounds };
      getRosterCycles(newPlanningCycle);
    } else {
      setRosterCycles(null);
    }
  }, [recurrenceRule, cycleWindow, rounds, getRosterCycles]);

  return (
    <Dialog
      title={t('recurrenceDialog.title')}
      open={open}
      aria-labelledby="recurrence-dialog"
      maxWidth="xl"
      onHidden={() => onHidden(false)}
      handleClose={() => onHidden(false)}
    >
      <PlanningCycleDialogSyles>
        <PlanningCycleDialogContent
          type={type}
          setRecurrenceRule={setRecurrenceRule}
          recurrenceRule={recurrenceRule}
          cycleWindow={cycleWindow}
          setCycleWindow={setCycleWindow}
          rounds={rounds}
          updateRound={updateRound}
          deleteRound={deleteRound}
          addRound={addRound}
          rosterCycles={rosterCycles}
        />
      </PlanningCycleDialogSyles>

      <DialogActions>
        <Button
          onClick={() => onHidden(false)}
          color="secondary"
          variant="contained"
        >
          {t('cancel')}
        </Button>
        <Button
          disabled={!recurrenceRule}
          onClick={handleSave}
          color="primary"
          variant="contained"
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
